/**
 * Extended Product Information – delivery countdown, size guide modal
 */
(function () {
  'use strict';

  function formatDeliveryDate(days) {
    const totalDays = parseInt(days, 10);
    if (!totalDays || totalDays < 1) return '';

    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + totalDays);

    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const weekday = date.toLocaleString('en-US', { weekday: 'short' }).toUpperCase();
    return `${day} ${month}, ${weekday}`;
  }

  function initDeliveryDate(root) {
    const el = root.querySelector('[data-epi-delivery-date]');
    if (!el || el.dataset.epiDeliveryDateReady === 'true') return;

    const days = el.dataset.deliveryDays;
    if (!days) return;

    const dateLabel = formatDeliveryDate(days);
    if (!dateLabel) return;

    el.dataset.epiDeliveryDateReady = 'true';
    const prefix = el.dataset.deliveryPrefix || 'DELIVERY BY';
    el.textContent = `${prefix} ${dateLabel}`;
  }

  function initCountdown(root) {
    const el = root.querySelector('[data-epi-countdown]');
    if (!el || el.dataset.epiCountdownReady === 'true') return;

    const minutes = parseInt(el.dataset.countdownMinutes, 10) || 50;
    const end = Date.now() + minutes * 60 * 1000;
    el.dataset.epiCountdownReady = 'true';

    function pad(n) {
      return String(n).padStart(2, '0');
    }

    function tick() {
      const diff = Math.max(0, end - Date.now());
      const totalSec = Math.floor(diff / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      el.innerHTML = `<span class="cpr-countdown__label">ORDER IN</span> ${pad(h)}H : ${pad(m)}M : ${pad(s)}S`;
      if (diff > 0) {
        window.requestAnimationFrame(() => {
          window.setTimeout(tick, 1000);
        });
      }
    }

    tick();
  }

  function initSizeGuide(sectionEl, root) {
    const openBtn = root.querySelector('[data-epi-size-guide-open]');
    const modal = sectionEl.querySelector('[data-epi-size-guide-modal]');
    if (!openBtn || !modal || modal.dataset.epiModalReady === 'true') return;

    modal.dataset.epiModalReady = 'true';
    const closeBtns = modal.querySelectorAll('[data-epi-size-guide-close]');

    function openModal() {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    openBtn.addEventListener('click', openModal);
    closeBtns.forEach((btn) => btn.addEventListener('click', closeModal));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });
  }

  function formatMoney(cents, format) {
    const moneyFormat =
      format ||
      window.theme?.money_format ||
      '${{amount}}';

    let value = cents;
    if (typeof value === 'string') {
      value = value.replace('.', '');
    }
    value = Number(value);
    if (Number.isNaN(value)) return '';

    const formatAmount = (amountCents, decimals = 2, thousandsSep = ',', decimalSep = '.') => {
      const fixed = (amountCents / 100).toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      return decimals > 0 && parts[1] ? `${parts[0]}${decimalSep}${parts[1]}` : parts[0];
    };

    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormat.match(placeholder);
    if (!match) return formatAmount(value);

    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = formatAmount(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = formatAmount(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatAmount(value, 0, '.', ',');
        break;
      case 'amount':
      default:
        formatted = formatAmount(value, 2);
        break;
    }

    return moneyFormat.replace(placeholder, formatted);
  }

  function getMoneyFormat(root) {
    return (
      root?.dataset?.epiMoneyFormat ||
      window.theme?.money_format ||
      '${{amount}}'
    );
  }

  function updatePrice(root, variant) {
    const priceEl = root.querySelector('[data-epi-price]');
    if (!priceEl || !variant) return;

    const compareEl = root.querySelector('[data-epi-compare]');
    const badgeEl = root.querySelector('[data-epi-badge]');
    const saveLabel = root.dataset.epiSaveLabel || 'Save';
    const onSale = variant.compare_at_price > variant.price;
    const moneyFormat = getMoneyFormat(root);

    priceEl.textContent = formatMoney(variant.price, moneyFormat);

    if (compareEl) {
      if (onSale) {
        compareEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.setAttribute('hidden', '');
      }
    }

    if (badgeEl) {
      if (onSale) {
        const saveCents = variant.compare_at_price - variant.price;
        badgeEl.textContent = `${saveLabel} ${formatMoney(saveCents, moneyFormat)}`;
        badgeEl.removeAttribute('hidden');
      } else {
        badgeEl.setAttribute('hidden', '');
      }
    }
  }

  async function performBuyItNow(form, buyNowBtn) {
    if (!form || !buyNowBtn || buyNowBtn.disabled) return;

    const idInput = form.querySelector('[name="id"]');
    const qtyInput = form.querySelector('[name="quantity"]');
    const variantId = idInput?.value;
    const quantity = Number(qtyInput?.value || 1);

    if (!variantId) return;

    const defaultLabel = buyNowBtn.dataset.defaultLabel || buyNowBtn.textContent.trim();
    buyNowBtn.disabled = true;
    buyNowBtn.textContent = '...';

    try {
      const rootRoute = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${rootRoute}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
        }),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || 'Could not add to cart');

      window.location.href = `${rootRoute}checkout`;
    } catch (error) {
      buyNowBtn.disabled = false;
      buyNowBtn.textContent = defaultLabel;
    }
  }

  function initBuyItNow(root) {
    const form = document.getElementById(`product-form-${root.dataset.epiSectionId}`);
    const sectionEl = root.closest('.extended-product-information-section');
    if (!form || !sectionEl) return;

    sectionEl.querySelectorAll('[data-epi-buy-it-now]').forEach((buyNowBtn) => {
      if (buyNowBtn.dataset.epiBuyNowReady === 'true') return;
      buyNowBtn.dataset.epiBuyNowReady = 'true';
      buyNowBtn.addEventListener('click', () => performBuyItNow(form, buyNowBtn));
    });
  }

  function updateAddButton(root, variant) {
    const form = document.getElementById(`product-form-${root.dataset.epiSectionId}`);
    if (!form) return;

    const sectionEl = root.closest('.extended-product-information-section');
    const addButton = form.querySelector('[name="add"]');
    const addButtonText = form.querySelector('[name="add"] staged-action-text');
    const buyNowBtns = sectionEl?.querySelectorAll('[data-epi-buy-it-now]') || [];
    const buttonsWrap = form.querySelector('.js-product-buttons');
    const soldOutLabel = root.dataset.epiSoldOutLabel || 'Sold out';
    const addLabel = root.dataset.epiAddLabel || 'Add to cart';

    if (!addButton || !addButtonText) return;

    if (!variant || !variant.available) {
      addButton.setAttribute('disabled', 'disabled');
      addButtonText.textContent = soldOutLabel;
      buyNowBtns.forEach((btn) => btn.setAttribute('disabled', 'disabled'));
      buttonsWrap?.classList.add('is-disabled');
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = addLabel;
      buyNowBtns.forEach((btn) => btn.removeAttribute('disabled'));
      buttonsWrap?.classList.remove('is-disabled');
    }

    updateStickyAddButton(root, variant);
  }

  function updateStickyBar(root, variant) {
    if (!variant) return;

    const stickyPrice = root.closest('.extended-product-information-section')?.querySelector(
      '[data-epi-sticky-price]'
    );
    if (stickyPrice) {
      stickyPrice.textContent = formatMoney(variant.price, getMoneyFormat(root));
    }

    const stickyImage = root.closest('.extended-product-information-section')?.querySelector(
      '[data-epi-sticky-image]'
    );
    if (stickyImage && stickyImage.tagName === 'IMG' && variant.featured_image?.src) {
      stickyImage.src = variant.featured_image.src;
    }
  }

  function updateStickyAddButton(root, variant) {
    const sectionEl = root.closest('.extended-product-information-section');
    const stickyBtn = sectionEl?.querySelector('[data-epi-sticky-submit]');
    const stickyText = stickyBtn?.querySelector('staged-action-text');
    if (!stickyBtn || !stickyText) return;

    const soldOutLabel = root.dataset.epiSoldOutLabel || 'Sold out';
    const addLabel = root.dataset.epiStickyAddLabel || 'Add to Cart';

    if (!variant || !variant.available) {
      stickyBtn.setAttribute('disabled', 'disabled');
      stickyText.textContent = soldOutLabel;
    } else {
      stickyBtn.removeAttribute('disabled');
      stickyText.textContent = addLabel;
    }
  }

  function findCustomerReviewsSection(productSectionEl) {
    const productSection = productSectionEl?.closest?.('.shopify-section');
    if (!productSection) return null;

    const sections = [...document.querySelectorAll('.shopify-section')];
    const productIndex = sections.indexOf(productSection);
    if (productIndex < 0) return null;

    for (let i = productIndex + 1; i < sections.length; i += 1) {
      const section = sections[i];

      if (section.querySelector('[data-epi-sticky-end]')) {
        return section;
      }

      const judgeWidget = section.querySelector(
        '.jdgm-reviews-widget, #judgeme_product_reviews, .jdgm-review-widget'
      );
      if (judgeWidget) {
        return section;
      }
    }

    return null;
  }

  function initStickyAtc(sectionEl, root) {
    const sticky = sectionEl.querySelector('[data-epi-sticky-atc]');
    const trigger = root.querySelector('[data-epi-sticky-trigger]');
    const stickyBtn = sectionEl.querySelector('[data-epi-sticky-submit]');
    if (!sticky || !trigger || sticky.dataset.epiStickyReady === 'true') return;

    sticky.dataset.epiStickyReady = 'true';

    let actionsOutOfView = false;

    const setStickyHeight = () => {
      document.documentElement.style.setProperty(
        '--epi-sticky-atc-height',
        `${sticky.offsetHeight}px`
      );
    };

    const isBeforeReviewSection = () => {
      const endEl = findCustomerReviewsSection(sectionEl);
      if (!endEl) return true;

      const rect = endEl.getBoundingClientRect();
      return rect.top > window.innerHeight;
    };

    const setStickyOffsetTargetsActive = (show) => {
      const backToTopButtons = document.querySelectorAll(
        '.back-to-top-button.back-to-top-button--dark.visible'
      );
      const shopifyChat = document.getElementById('ShopifyChat');
      const inboxChat = document.querySelector('inbox-online-store-chat');
      const chatEl = shopifyChat || inboxChat;

      backToTopButtons.forEach((el) => el.classList.toggle('position-active', show));
      if (shopifyChat) shopifyChat.classList.toggle('position-active', show);
      if (inboxChat) inboxChat.classList.toggle('position-active', show);

      if (!show) {
        document.documentElement.style.setProperty('--epi-floating-chat-height', '0px');
        return;
      }

      const chatHeight = chatEl
        ? Math.ceil(chatEl.getBoundingClientRect().height) || 56
        : 0;
      document.documentElement.style.setProperty(
        '--epi-floating-chat-height',
        `${chatHeight}px`
      );
    };

    const updateStickyVisibility = () => {
      const show = actionsOutOfView && isBeforeReviewSection();
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.body.classList.toggle('epi-sticky-atc-active', show);
      if (show) setStickyHeight();
      setStickyOffsetTargetsActive(show);
    };

    const triggerObserver = new IntersectionObserver(
      ([entry]) => {
        actionsOutOfView = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        updateStickyVisibility();
      },
      { threshold: [0, 0.01] }
    );
    triggerObserver.observe(trigger);

    window.addEventListener('scroll', updateStickyVisibility, { passive: true });
    window.addEventListener('resize', () => {
      setStickyHeight();
      updateStickyVisibility();
    });
    setStickyHeight();
    updateStickyVisibility();

    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const form = document.getElementById(`product-form-${root.dataset.epiSectionId}`);
        const mainBtn = form?.querySelector('.js-product-add');
        if (!mainBtn || mainBtn.disabled) return;

        const stickyStaged = stickyBtn.querySelector('staged-action');
        const mainStaged = mainBtn.querySelector('staged-action');

        if (stickyStaged && mainStaged) {
          const syncState = () => {
            if (mainStaged.classList.contains('is-doing')) {
              stickyStaged.setState('DOING');
            } else if (mainStaged.classList.contains('is-done')) {
              stickyStaged.setState('DONE');
            } else if (
              !mainStaged.classList.contains('is-doing') &&
              !mainStaged.classList.contains('is-done')
            ) {
              stickyStaged.setState('IDLE');
            }
          };

          const observer = new MutationObserver(syncState);
          observer.observe(mainStaged, { attributes: true, attributeFilter: ['class'] });
          window.setTimeout(() => observer.disconnect(), 5000);
        }

        mainBtn.click();
      });
    }
  }

  /**
   * KerryYoo customization (2026-07-03): Walmart-style variant media groups.
   * When the selected variant's featured image changes, re-fetch this section
   * via the Section Rendering API and swap the gallery column, so the gallery
   * only shows the media group of the selected color (the filtering itself
   * happens in Liquid — see snippets/product-gallery.liquid).
   */
  let galleryRefreshController = null;

  function refreshGalleryForVariant(root, variant) {
    const sectionEl = root.closest('.extended-product-information-section');
    const galleryCol = sectionEl?.querySelector('.epi__gallery-col');
    if (!galleryCol || !variant) return;

    const newFeaturedId = String(
      variant.featured_media?.id || variant.featured_image?.id || ''
    );
    if (!newFeaturedId) return;
    if (root.dataset.epiGalleryFeaturedId === newFeaturedId) return;
    root.dataset.epiGalleryFeaturedId = newFeaturedId;

    const sectionId = root.dataset.epiSectionId;
    if (!sectionId) return;

    if (!document.getElementById('epi-gallery-refresh-style')) {
      const style = document.createElement('style');
      style.id = 'epi-gallery-refresh-style';
      style.textContent =
        '.epi-gallery-refreshing{opacity:.45;pointer-events:none;transition:opacity .15s ease}';
      document.head.appendChild(style);
    }

    if (galleryRefreshController) galleryRefreshController.abort();
    galleryRefreshController = new AbortController();
    const signal = galleryRefreshController.signal;

    const sectionUrl = `${window.location.pathname}?variant=${variant.id}&section_id=${sectionId}`;
    const pageUrl = `${window.location.pathname}?variant=${variant.id}`;
    galleryCol.classList.add('epi-gallery-refreshing');

    const fetchGalleryCol = async (url) => {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Render failed: ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const col = doc.querySelector('.epi__gallery-col');
      // Only accept a render that actually contains the requested variant's
      // featured media — protects against transient wrong/default renders.
      if (!col || !col.querySelector(`[data-media-id="${newFeaturedId}"]`)) {
        throw new Error('Render does not match requested variant');
      }
      return col;
    };

    (async () => {
      let newCol = null;
      try {
        newCol = await fetchGalleryCol(sectionUrl);
      } catch (sectionError) {
        if (signal.aborted) return;
        try {
          newCol = await fetchGalleryCol(pageUrl);
        } catch (pageError) {
          newCol = null;
        }
      }
      if (signal.aborted) return;

      const currentCol = sectionEl.querySelector('.epi__gallery-col');
      if (!newCol || !currentCol) {
        // Leave the current gallery in place and allow a retry on the next
        // variant change (including re-selecting the same color).
        root.dataset.epiGalleryFeaturedId = '';
        currentCol?.classList.remove('epi-gallery-refreshing');
        return;
      }

      currentCol.replaceWith(newCol);
      window.requestAnimationFrame(() => {
        initGalleryNav(root);
        window.dispatchEvent(new Event('resize'));
      });
    })();
  }

  function initVariantChange(root) {
    const selectors = root.querySelectorAll(
      'variant-radios, variant-selects, variant-mixed-inputs'
    );
    if (!selectors.length) return;

    selectors.forEach((selector) => {
      if (selector.dataset.epiVariantBound === 'true') return;
      selector.dataset.epiVariantBound = 'true';

      selector.addEventListener('on:variant:change', (event) => {
        const variant = event.detail?.variant;
        if (!variant) return;
        updatePrice(root, variant);
        updateAddButton(root, variant);
        updateStickyBar(root, variant);
        refreshGalleryForVariant(root, variant);
      });
    });
  }

  function syncGalleryThumbHeight(root) {
    const asideGallery = root.querySelector('.media-gallery--thumbnails-aside');
    if (!asideGallery) return null;

    const slider = asideGallery.querySelector('.media-gallery__slider');
    const nav = asideGallery.querySelector('.media-gallery__nav');
    const thumbList = asideGallery.querySelector('.js-media-gallery-thumb-list');
    if (!slider || !nav) return null;

    const apply = () => {
      const stageHeight = slider.getBoundingClientRect().height;
      if (stageHeight < 80) return;

      asideGallery.style.setProperty('--epi-gallery-stage-height', `${Math.round(stageHeight)}px`);
      nav.style.height = `${Math.round(stageHeight)}px`;
      nav.style.maxHeight = `${Math.round(stageHeight)}px`;

      nav.classList.add('is-loaded');

      thumbList?.querySelectorAll('image-skeleton + img').forEach((img) => {
        const skeleton = img.previousElementSibling;
        if (skeleton?.tagName === 'IMAGE-SKELETON' && !skeleton.hasAttribute('loaded')) {
          skeleton.setAttribute('loaded', '');
        }
        img.style.opacity = '1';
      });

      window.dispatchEvent(new Event('resize'));
    };

    apply();
    [100, 350, 700, 1200].forEach((delay) => window.setTimeout(apply, delay));

    slider.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', apply, { once: true });
      }
    });

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => window.requestAnimationFrame(apply));
      observer.observe(slider);
      asideGallery.dataset.epiGalleryResizeBound = 'true';
      return observer;
    }

    window.addEventListener('resize', apply);
    return null;
  }

  function initGalleryNav(root) {
    const nav = root.querySelector('.media-gallery__nav');
    if (!nav) return;

    const reveal = () => {
      nav.classList.add('is-loaded');
      syncGalleryThumbHeight(root);
    };

    const gallery = root.querySelector('media-gallery');
    const carousel = gallery?.querySelector('media-carousel');

    syncGalleryThumbHeight(root);

    if (carousel) {
      carousel.addEventListener('on:media-carousel:init', reveal, { once: true });
    }

    if (gallery && typeof gallery.goToSlide === 'function') {
      reveal();
      return;
    }

    const observer = new MutationObserver(() => {
      if (root.querySelector('media-gallery') || nav.querySelector('.thumbnail-list__item')) {
        reveal();
        observer.disconnect();
      }
    });
    observer.observe(root, { childList: true, subtree: true });
    window.setTimeout(reveal, 2500);
  }

  function initSection(sectionEl) {
    const root = sectionEl.querySelector('[data-extended-product-information]');
    if (!root || root.dataset.epiReady === 'true') return;
    root.dataset.epiReady = 'true';
    initDeliveryDate(root);
    initCountdown(root);
    initSizeGuide(sectionEl, root);
    initBuyItNow(root);
    initVariantChange(root);
    initGalleryNav(root);
    initStickyAtc(sectionEl, root);
  }

  function initAll() {
    document.querySelectorAll('.extended-product-information-section').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('extended-product-information-section')) {
      const root = event.target.querySelector('[data-extended-product-information]');
      if (root) root.dataset.epiReady = 'false';
      initSection(event.target);
    }
  });
})();
